import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';

import FormModel from '../../../../models/Form';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldId from '../../../../types/FormFieldId';
import FormFieldType from '../../../../types/FormFieldType';
import { normalizeBooleanField } from '../../../../util/checkoutFormUtils';
import { fieldTypeLabel, labels } from '../../../../util/formLabels';
import globalTranslations from '../../../../i18n/globalTranslations';
import FormGroupField from '../../../form/FormGroupField';
import SelectField from '../../../form/SelectField';
import Switch from '../../../common/Switch';

const messages = defineMessages({
  phonePlaceholder: {
    id: 'checkout.deliveryForm.phonePlaceholder',
    defaultMessage: "Leave blank if same as the buyer's number",
  },
});

const getDeliveryFormLabels = () => ({
  [FormFieldId.DELIVERY_COMPANY]: labels.deliveryCompany,
  [FormFieldId.DELIVERY_FIRST_NAME]: labels.firstName,
  [FormFieldId.DELIVERY_LAST_NAME]: labels.lastName,
  [FormFieldId.DELIVERY_STREET_ADDRESS]: labels.deliveryStreetAddress,
  [FormFieldId.DELIVERY_POSTAL_CODE]: labels.deliveryPostalCode,
  [FormFieldId.DELIVERY_CITY]: labels.deliveryCity,
  [FormFieldId.DELIVERY_COUNTRY]: labels.country,
  [FormFieldId.DELIVERY_PHONE]: labels.deliveryTelephone,
  [FormFieldId.DELIVERY_CARE_OF]: labels.deliveryCareOf,
  [FormFieldId.DELIVERY_ADDRESS_DROPDOWN]: labels.deliveryAddressId,
});

const deliveryFieldLabels = {
  [FormFieldId.DELIVERY_COMPANY]: fieldTypeLabel.DELIVERY_COMPANY,
  [FormFieldId.DELIVERY_FIRST_NAME]: fieldTypeLabel.FIRSTNAME,
  [FormFieldId.DELIVERY_LAST_NAME]: fieldTypeLabel.LASTNAME,
  [FormFieldId.DELIVERY_STREET_ADDRESS]: fieldTypeLabel.DELIVERY_STREET_ADDRESS,
  [FormFieldId.DELIVERY_POSTAL_CODE]: fieldTypeLabel.DELIVERY_POSTAL_CODE,
  [FormFieldId.DELIVERY_CITY]: fieldTypeLabel.DELIVERY_CITY,
  [FormFieldId.DELIVERY_COUNTRY]: fieldTypeLabel.DELIVERY_COUNTRY,
  [FormFieldId.DELIVERY_PHONE]: fieldTypeLabel.DELIVERY_PHONE,
  [FormFieldId.DELIVERY_CARE_OF]: fieldTypeLabel.DELIVERY_CARE_OF,
};

const validPickupPointFields = new Set([
  FormFieldId.DELIVERY_POSTAL_CODE,
  FormFieldId.DELIVERY_STREET_ADDRESS,
  FormFieldId.DELIVERY_CITY,
]);

// Layout order for flex wrap: name row, then street, then postal+city+phone, then rest. No gaps.
const DELIVERY_LAYOUT_ORDER = [
  FormFieldId.DELIVERY_COMPANY,
  FormFieldId.DELIVERY_FIRST_NAME,
  FormFieldId.DELIVERY_LAST_NAME,
  FormFieldId.DELIVERY_STREET_ADDRESS,
  FormFieldId.DELIVERY_POSTAL_CODE,
  FormFieldId.DELIVERY_CITY,
  FormFieldId.DELIVERY_PHONE,
  FormFieldId.DELIVERY_COUNTRY,
  FormFieldId.DELIVERY_CARE_OF,
];

const DELIVERY_FIELD_WIDTH_CLASS = {
  [FormFieldId.DELIVERY_STREET_ADDRESS]: 'full',
  [FormFieldId.DELIVERY_CARE_OF]: 'full',
};

const CheckoutDeliveryForm = ({
  checkoutStore,
  onOrderUpdate,
  deliveryForm,
  showNewAddressButton,
  onNewAddressClick,
  hideDeliveryAddressDropdown,
  sectionTitle,
  showSaveToAddressBookSwitch,
  deferSaveToAddressBookSwitch,
  saveToAddressBookSwitchChecked,
  onSaveToAddressBookSwitchIntentChange,
  intl,
}) => {
  const { cancelOrder, shippingInfoIsLoading, order } = checkoutStore;
  const { getCustomerField } = order;
  const disabled = shippingInfoIsLoading;

  // Check if DELIVERY_CARE_OF field exists and has a value to determine initial state
  const careOfField = deliveryForm.getField(FormFieldId.DELIVERY_CARE_OF);
  const hasCareOfValue =
    careOfField && careOfField.value && careOfField.value.trim() !== '';
  const [showCareOfField, setShowCareOfField] = useState(hasCareOfValue);

  /** Order `customer_fields` only — if the key is absent, do not show the company switch. */
  const deliveryIsCompanyCustomerField = order.customer_fields?.get(
    FormFieldId.DELIVERY_IS_COMPANY
  );
  const showDeliveryIsCompanyToggle = Boolean(deliveryIsCompanyCustomerField);
  const isDeliveryCompanyCheckout = normalizeBooleanField(
    deliveryIsCompanyCustomerField?.value
  );

  const updateOrder = ({ fetchPickupPoints }) => {
    onOrderUpdate({ withoutTimer: true, fetchPickupPoints });
  };

  const oneTimeDeliveryField = getCustomerField(
    FormFieldId.ONE_TIME_DELIVERY_ADDRESS
  ).field;

  const handleSaveToAddressBookSwitchChange = (checked) => {
    if (deferSaveToAddressBookSwitch && onSaveToAddressBookSwitchIntentChange) {
      onSaveToAddressBookSwitchIntentChange(!!checked);
      return;
    }
    cancelOrder();
    if (oneTimeDeliveryField) {
      oneTimeDeliveryField.setValue(!checked);
    }
    onOrderUpdate({ withoutTimer: true });
  };

  const saveToAddressBookSwitchDisplayChecked =
    deferSaveToAddressBookSwitch && saveToAddressBookSwitchChecked != null
      ? saveToAddressBookSwitchChecked
      : !normalizeBooleanField(oneTimeDeliveryField?.value);

  const handleCustomerFieldChange = (e, fieldId) => {
    cancelOrder();
    const orderField = getCustomerField(fieldId).field;
    const rawValue = e.target.value;

    if (fieldId === FormFieldId.DELIVERY_COUNTRY) {
      if (rawValue === '') {
        orderField.setValue(null);
      } else {
        const asNum = Number(rawValue);
        orderField.setValue(Number.isNaN(asNum) ? rawValue : asNum);
      }
      return;
    }

    orderField.setValue(rawValue === '' ? null : rawValue);
  };

  const handleDeliveryAddressDropdownChange = (e, field) => {
    cancelOrder();
    const value = e.target.value;
    const orderField = getCustomerField(
      FormFieldId.DELIVERY_ADDRESS_DROPDOWN
    ).field;
    const fieldValue = value === '' ? null : Number(value);
    orderField.setValue(fieldValue);
    field.setValue(value);
    updateOrder({ fetchPickupPoints: false });
  };

  const handleBlur = (_, field) => {
    deliveryForm.validateField(field.id);

    // User has no input, so no update is needed
    if (
      (field.value == null || field.value === '') &&
      field.value === field.previousValue
    ) {
      return;
    }
    // User has no input
    if (field.value !== field.previousValue && field.previousValue == null) {
      field.updatePreviousValue();
      return;
    }

    // Standard checkout update for pickup point fields
    if (validPickupPointFields.has(field.id)) {
      updateOrder({ fetchPickupPoints: field.valid });
      return;
    }

    // Standard checkout update for other fields
    field.updatePreviousValue();
    updateOrder({ fetchPickupPoints: false });
  };

  const updateCustomerType = (checked) => {
    if (!deliveryIsCompanyCustomerField) {
      return;
    }
    const booleanValue = !!checked;
    deliveryIsCompanyCustomerField.setValue(booleanValue);
    const formMirror = deliveryForm.getField(FormFieldId.DELIVERY_IS_COMPANY);
    if (formMirror) {
      formMirror.setValue(booleanValue);
    }
    onOrderUpdate({ withoutTimer: true });
  };

  return (
    <div className="CheckoutDeliveryForm">
      <div className="CheckoutDeliveryForm__title">
        {sectionTitle != null ? (
          sectionTitle
        ) : (
          <FormattedMessage {...globalTranslations.recipientInformationTitle} />
        )}
      </div>
      <div className="CheckoutDeliveryForm__form-inner">
        {showDeliveryIsCompanyToggle ? (
          <Row>
            <Col>
              <div className="CheckoutDeliveryForm__toggle-row">
                <Switch
                  checked={normalizeBooleanField(
                    deliveryIsCompanyCustomerField.value
                  )}
                  onChange={(e) => updateCustomerType(e.target.checked)}
                  size="xs"
                  disabled={disabled}
                >
                  <FormattedMessage
                    {...globalTranslations.recipientIsCompanyLabel}
                  />
                </Switch>
              </div>
            </Col>
          </Row>
        ) : null}
        {deliveryForm.hasFields &&
          (() => {
            const deliveryFormFields = deliveryForm.getFormFields(
              new Set([FormFieldType.BOOLEAN, FormFieldType.BUTTON])
            );
            const availableIds = new Set(
              deliveryFormFields.filter((f) => f.active).map((f) => f.id)
            );
            const deliveryFormLabels = getDeliveryFormLabels();

            const renderDeliveryField = (field, widthClass) => {
              if (!field.active) return null;
              const fieldId = field.id;
              const wrapperClass = `CheckoutDeliveryForm__field CheckoutDeliveryForm__field--${widthClass}`;

              if (fieldId === FormFieldId.DELIVERY_CARE_OF) {
                const hasValue = field.value && field.value.trim() !== '';
                const shouldShowField = showCareOfField || hasValue;

                if (!shouldShowField) {
                  return (
                    <div className={wrapperClass} key={fieldId}>
                      <div className="CheckoutDeliveryForm__care-of-link">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowCareOfField(true);
                          }}
                          className="CheckoutDeliveryForm__add-care-of-link"
                        >
                          {labels.addCareOf}
                        </a>
                      </div>
                    </div>
                  );
                }
              }

              if (fieldId === FormFieldId.DELIVERY_ADDRESS_DROPDOWN) {
                if (hideDeliveryAddressDropdown) {
                  return null;
                }
                const options = field.options || [];
                const optionCount = options.length;
                const showDropdown = optionCount > 0;
                const showAddButton =
                  showNewAddressButton &&
                  typeof onNewAddressClick === 'function';

                if (!showDropdown && !showAddButton) return null;

                if (!showDropdown && showAddButton) {
                  return (
                    <div className={wrapperClass} key={fieldId}>
                      <div className="CheckoutDeliveryForm__delivery-actions">
                        <Button
                          color="secondary"
                          onClick={onNewAddressClick}
                          className="CheckoutDeliveryForm__new-address-button"
                        >
                          <FormattedMessage
                            {...globalTranslations.addNewAddressTitle}
                          />
                        </Button>
                      </div>
                    </div>
                  );
                }

                const orderDropdownField = getCustomerField(
                  FormFieldId.DELIVERY_ADDRESS_DROPDOWN
                )?.field;
                const rawOrderVal = orderDropdownField?.value;
                const selectedIdStr =
                  rawOrderVal !== null && rawOrderVal !== undefined
                    ? String(rawOrderVal)
                    : null;

                const apiOptions = options.map((o) => ({
                  value: String(o.value),
                  label: o.label,
                }));
                const apiValues = new Set(apiOptions.map((o) => o.value));
                const orphanSelected =
                  selectedIdStr &&
                  !apiValues.has(selectedIdStr) &&
                  selectedIdStr !== '';
                const orphanOption = orphanSelected
                  ? [{ value: selectedIdStr, label: selectedIdStr }]
                  : [];
                const selectOptions = [...orphanOption, ...apiOptions];
                return (
                  <div className={wrapperClass} key={fieldId}>
                    <div className="CheckoutDeliveryForm__delivery-actions">
                      <SelectField
                        field={field}
                        fieldName={fieldId}
                        formName="checkout"
                        floatingLabel
                        label={
                          deliveryFormLabels[
                            FormFieldId.DELIVERY_ADDRESS_DROPDOWN
                          ]
                        }
                        options={selectOptions}
                        required={field.required}
                        onChange={(e) =>
                          handleDeliveryAddressDropdownChange(e, field)
                        }
                        onFocus={cancelOrder}
                      />
                      {showAddButton && (
                        <Button
                          color="secondary"
                          onClick={onNewAddressClick}
                          className="CheckoutDeliveryForm__new-address-button"
                        >
                          <FormattedMessage
                            {...globalTranslations.addNewAddressTitle}
                          />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              }

              if (field.type === FormFieldType.SELECT) {
                const options = (field.options || []).map((o) => ({
                  value: String(o.value),
                  label: o.label,
                }));

                return (
                  <div className={wrapperClass} key={fieldId}>
                    <SelectField
                      field={field}
                      fieldName={fieldId}
                      formName={'checkout'}
                      floatingLabel
                      label={deliveryFormLabels[fieldId]}
                      options={options}
                      required={field.required}
                      onChange={(e) => handleCustomerFieldChange(e, fieldId)}
                      onBlur={handleBlur}
                      onFocus={cancelOrder}
                    />
                  </div>
                );
              }

              let type;
              let description;

              if (field.type === FormFieldType.PHONE) {
                type = 'tel';
                description = (
                  <small className="form-text text-muted">
                    <FormattedMessage {...messages.phonePlaceholder} />
                  </small>
                );
              } else {
                type = field.type.toLowerCase();
              }

              return (
                <div className={wrapperClass} key={fieldId}>
                  <FormGroupField
                    field={field}
                    fieldName={deliveryFieldLabels[fieldId]}
                    formName={'checkout'}
                    floatingLabel
                    label={deliveryFormLabels[fieldId]}
                    required={field.required}
                    maxLength={field.max_length}
                    minLength={field.min_length}
                    type={type}
                    description={description}
                    onChange={(e) => handleCustomerFieldChange(e, fieldId)}
                    onBlur={handleBlur}
                    onFocus={cancelOrder}
                  />
                </div>
              );
            };

            const deliveryNodes = (() => {
              const nodes = [];
              let skipNextLastName = false;
              let skipNextCity = false;
              let skipNextPhone = false;
              let skipNextCountry = false;
              for (const fieldId of DELIVERY_LAYOUT_ORDER) {
                if (!availableIds.has(fieldId)) continue;
                if (
                  fieldId === FormFieldId.DELIVERY_LAST_NAME &&
                  skipNextLastName
                ) {
                  skipNextLastName = false;
                  continue;
                }
                if (fieldId === FormFieldId.DELIVERY_CITY && skipNextCity) {
                  skipNextCity = false;
                  continue;
                }
                if (fieldId === FormFieldId.DELIVERY_PHONE && skipNextPhone) {
                  skipNextPhone = false;
                  continue;
                }
                if (
                  fieldId === FormFieldId.DELIVERY_COUNTRY &&
                  skipNextCountry
                ) {
                  skipNextCountry = false;
                  continue;
                }
                if (fieldId === FormFieldId.DELIVERY_COMPANY) {
                  nodes.push({ type: 'company-row' });
                  continue;
                }
                if (fieldId === FormFieldId.DELIVERY_FIRST_NAME) {
                  if (
                    availableIds.has(FormFieldId.DELIVERY_LAST_NAME) &&
                    availableIds.has(FormFieldId.DELIVERY_PHONE)
                  ) {
                    nodes.push({ type: 'name-row' });
                    skipNextLastName = true;
                    skipNextPhone = true;
                    continue;
                  }
                }
                if (
                  fieldId === FormFieldId.DELIVERY_POSTAL_CODE &&
                  availableIds.has(FormFieldId.DELIVERY_CITY)
                ) {
                  nodes.push({ type: 'postal-row' });
                  skipNextCity = true;
                  if (availableIds.has(FormFieldId.DELIVERY_COUNTRY)) {
                    skipNextCountry = true;
                  }
                  continue;
                }
                nodes.push({ type: 'field', fieldId });
              }
              return nodes;
            })();

            const renderDeliveryCompanyRow = () => {
              const companyField = deliveryForm.getField(
                FormFieldId.DELIVERY_COMPANY
              );
              if (!companyField?.active) return null;
              return (
                <div
                  className="CheckoutDeliveryForm__field-row CheckoutDeliveryForm__field-row--company"
                  key="delivery-company-row"
                >
                  <div className="CheckoutDeliveryForm__field-row__company">
                    <FormGroupField
                      field={companyField}
                      fieldName={
                        deliveryFieldLabels[FormFieldId.DELIVERY_COMPANY]
                      }
                      formName={'checkout'}
                      floatingLabel
                      label={deliveryFormLabels[FormFieldId.DELIVERY_COMPANY]}
                      required={companyField.required}
                      maxLength={companyField.max_length}
                      minLength={companyField.min_length}
                      type="text"
                      onChange={(e) =>
                        handleCustomerFieldChange(
                          e,
                          FormFieldId.DELIVERY_COMPANY
                        )
                      }
                      onBlur={handleBlur}
                      onFocus={cancelOrder}
                    />
                  </div>
                </div>
              );
            };

            const renderDeliveryNameRow = () => {
              const firstNameField = deliveryForm.getField(
                FormFieldId.DELIVERY_FIRST_NAME
              );
              const lastNameField = deliveryForm.getField(
                FormFieldId.DELIVERY_LAST_NAME
              );
              const phoneField = deliveryForm.getField(
                FormFieldId.DELIVERY_PHONE
              );
              if (
                !firstNameField?.active &&
                !lastNameField?.active &&
                !phoneField?.active
              )
                return null;
              return (
                <div
                  className="CheckoutDeliveryForm__field-row CheckoutDeliveryForm__field-row--name"
                  key="delivery-name-row"
                >
                  {firstNameField?.active && (
                    <div className="CheckoutDeliveryForm__field-row__first-name">
                      <FormGroupField
                        field={firstNameField}
                        fieldName={
                          deliveryFieldLabels[FormFieldId.DELIVERY_FIRST_NAME]
                        }
                        formName={'checkout'}
                        floatingLabel
                        label={labels.firstName}
                        required={
                          firstNameField.required && !isDeliveryCompanyCheckout
                        }
                        maxLength={firstNameField.max_length}
                        minLength={firstNameField.min_length}
                        type="text"
                        validate={!isDeliveryCompanyCheckout}
                        onChange={(e) =>
                          handleCustomerFieldChange(
                            e,
                            FormFieldId.DELIVERY_FIRST_NAME
                          )
                        }
                        onBlur={handleBlur}
                        onFocus={cancelOrder}
                      />
                    </div>
                  )}
                  {lastNameField?.active && (
                    <div className="CheckoutDeliveryForm__field-row__last-name">
                      <FormGroupField
                        field={lastNameField}
                        fieldName={
                          deliveryFieldLabels[FormFieldId.DELIVERY_LAST_NAME]
                        }
                        formName={'checkout'}
                        floatingLabel
                        label={
                          deliveryFormLabels[FormFieldId.DELIVERY_LAST_NAME]
                        }
                        required={
                          lastNameField.required && !isDeliveryCompanyCheckout
                        }
                        maxLength={lastNameField.max_length}
                        minLength={lastNameField.min_length}
                        type="text"
                        validate={!isDeliveryCompanyCheckout}
                        onChange={(e) =>
                          handleCustomerFieldChange(
                            e,
                            FormFieldId.DELIVERY_LAST_NAME
                          )
                        }
                        onBlur={handleBlur}
                        onFocus={cancelOrder}
                      />
                    </div>
                  )}
                  {phoneField?.active && (
                    <div className="CheckoutDeliveryForm__field-row__phone">
                      <FormGroupField
                        field={phoneField}
                        fieldName={
                          deliveryFieldLabels[FormFieldId.DELIVERY_PHONE]
                        }
                        formName={'checkout'}
                        floatingLabel
                        label={deliveryFormLabels[FormFieldId.DELIVERY_PHONE]}
                        required={phoneField.required}
                        maxLength={phoneField.max_length}
                        minLength={phoneField.min_length}
                        type="tel"
                        description={
                          <small className="form-text CheckoutDeliveryForm__phone-description">
                            <FormattedMessage {...messages.phonePlaceholder} />
                          </small>
                        }
                        onChange={(e) =>
                          handleCustomerFieldChange(
                            e,
                            FormFieldId.DELIVERY_PHONE
                          )
                        }
                        onBlur={handleBlur}
                        onFocus={cancelOrder}
                      />
                    </div>
                  )}
                </div>
              );
            };

            const renderDeliveryPostalRow = () => {
              const postalField = deliveryForm.getField(
                FormFieldId.DELIVERY_POSTAL_CODE
              );
              const cityField = deliveryForm.getField(
                FormFieldId.DELIVERY_CITY
              );
              const countryField = deliveryForm.getField(
                FormFieldId.DELIVERY_COUNTRY
              );
              if (
                !postalField?.active &&
                !cityField?.active &&
                !countryField?.active
              )
                return null;
              const hasCountry = countryField?.active;
              return (
                <div
                  className={classNames(
                    'CheckoutDeliveryForm__field-row',
                    'CheckoutDeliveryForm__field-row--postal',
                    {
                      'CheckoutDeliveryForm__field-row--postal-no-country':
                        !hasCountry,
                    }
                  )}
                  key="delivery-postal-row"
                >
                  {postalField?.active && (
                    <div className="CheckoutDeliveryForm__field-row__postal-code">
                      <FormGroupField
                        field={postalField}
                        fieldName={
                          deliveryFieldLabels[FormFieldId.DELIVERY_POSTAL_CODE]
                        }
                        formName={'checkout'}
                        floatingLabel
                        label={
                          deliveryFormLabels[FormFieldId.DELIVERY_POSTAL_CODE]
                        }
                        required={postalField.required}
                        maxLength={postalField.max_length}
                        minLength={postalField.min_length}
                        type="text"
                        onChange={(e) =>
                          handleCustomerFieldChange(
                            e,
                            FormFieldId.DELIVERY_POSTAL_CODE
                          )
                        }
                        onBlur={handleBlur}
                        onFocus={cancelOrder}
                      />
                    </div>
                  )}
                  {cityField?.active && (
                    <div className="CheckoutDeliveryForm__field-row__city">
                      <FormGroupField
                        field={cityField}
                        fieldName={
                          deliveryFieldLabels[FormFieldId.DELIVERY_CITY]
                        }
                        formName={'checkout'}
                        floatingLabel
                        label={deliveryFormLabels[FormFieldId.DELIVERY_CITY]}
                        required={cityField.required}
                        maxLength={cityField.max_length}
                        minLength={cityField.min_length}
                        type="text"
                        onChange={(e) =>
                          handleCustomerFieldChange(
                            e,
                            FormFieldId.DELIVERY_CITY
                          )
                        }
                        onBlur={handleBlur}
                        onFocus={cancelOrder}
                      />
                    </div>
                  )}
                  {countryField?.active &&
                    (countryField.type === FormFieldType.SELECT ? (
                      <div className="CheckoutDeliveryForm__field-row__country">
                        <SelectField
                          field={countryField}
                          fieldName={FormFieldId.DELIVERY_COUNTRY}
                          formName={'checkout'}
                          floatingLabel
                          label={
                            deliveryFormLabels[FormFieldId.DELIVERY_COUNTRY]
                          }
                          options={(countryField.options || []).map((o) => ({
                            value: String(o.value),
                            label: o.label,
                          }))}
                          required={countryField.required}
                          onChange={(e) =>
                            handleCustomerFieldChange(
                              e,
                              FormFieldId.DELIVERY_COUNTRY
                            )
                          }
                          onBlur={handleBlur}
                          onFocus={cancelOrder}
                        />
                      </div>
                    ) : (
                      <div className="CheckoutDeliveryForm__field-row__country">
                        <FormGroupField
                          field={countryField}
                          fieldName={
                            deliveryFieldLabels[FormFieldId.DELIVERY_COUNTRY]
                          }
                          formName={'checkout'}
                          floatingLabel
                          label={
                            deliveryFormLabels[FormFieldId.DELIVERY_COUNTRY]
                          }
                          required={countryField.required}
                          maxLength={countryField.max_length}
                          minLength={countryField.min_length}
                          type="text"
                          onChange={(e) =>
                            handleCustomerFieldChange(
                              e,
                              FormFieldId.DELIVERY_COUNTRY
                            )
                          }
                          onBlur={handleBlur}
                          onFocus={cancelOrder}
                        />
                      </div>
                    ))}
                </div>
              );
            };

            return (
              <div className="CheckoutDeliveryForm__fields">
                {deliveryNodes.map((node) => {
                  if (node.type === 'company-row') {
                    return (
                      <React.Fragment key="delivery-company-row">
                        {renderDeliveryCompanyRow()}
                      </React.Fragment>
                    );
                  }
                  if (node.type === 'name-row') {
                    const nameRow = renderDeliveryNameRow();
                    if (!nameRow) return null;
                    const isDeliveryCompany = getCustomerField(
                      FormFieldId.DELIVERY_IS_COMPANY
                    ).value;
                    if (isDeliveryCompany) {
                      return (
                        <div
                          className="CheckoutDeliveryForm__contact-section"
                          key="delivery-name-row"
                        >
                          <div className="CheckoutDeliveryForm__contact-heading">
                            <FormattedMessage
                              {...globalTranslations.contactSectionTitle}
                            />
                          </div>
                          {nameRow}
                        </div>
                      );
                    }
                    return (
                      <React.Fragment key="delivery-name-row">
                        {nameRow}
                      </React.Fragment>
                    );
                  }
                  if (node.type === 'postal-row') {
                    return (
                      <React.Fragment key="delivery-postal-row">
                        {renderDeliveryPostalRow()}
                      </React.Fragment>
                    );
                  }
                  const field = deliveryForm.getField(node.fieldId);
                  if (!field) return null;
                  const widthClass =
                    DELIVERY_FIELD_WIDTH_CLASS[node.fieldId] || 'full';
                  return renderDeliveryField(field, widthClass);
                })}
                {[...availableIds]
                  .filter((id) => !DELIVERY_LAYOUT_ORDER.includes(id))
                  .map((fieldId) => {
                    const field = deliveryForm.getField(fieldId);
                    if (!field) return null;
                    return renderDeliveryField(field, 'full');
                  })}
              </div>
            );
          })()}
        {showSaveToAddressBookSwitch && oneTimeDeliveryField ? (
          <Row>
            <Col>
              <div className="CheckoutDeliveryForm__toggle-row CheckoutDeliveryForm__save-to-address-book-row">
                <Switch
                  checked={saveToAddressBookSwitchDisplayChecked}
                  onChange={(e) =>
                    handleSaveToAddressBookSwitchChange(!!e.target.checked)
                  }
                  size="xs"
                  disabled={disabled}
                >
                  <FormattedMessage
                    {...globalTranslations.saveAddressForLaterUse}
                  />
                </Switch>
              </div>
            </Col>
          </Row>
        ) : null}
      </div>
    </div>
  );
};

CheckoutDeliveryForm.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  deliveryForm: modelOf(FormModel).isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  showNewAddressButton: PropTypes.bool,
  onNewAddressClick: PropTypes.func,
  hideDeliveryAddressDropdown: PropTypes.bool,
  sectionTitle: PropTypes.node,
  showSaveToAddressBookSwitch: PropTypes.bool,
  deferSaveToAddressBookSwitch: PropTypes.bool,
  saveToAddressBookSwitchChecked: PropTypes.bool,
  onSaveToAddressBookSwitchIntentChange: PropTypes.func,
  intl: intlShape.isRequired,
};

CheckoutDeliveryForm.defaultProps = {
  showNewAddressButton: false,
  onNewAddressClick: undefined,
  hideDeliveryAddressDropdown: false,
  sectionTitle: null,
  showSaveToAddressBookSwitch: false,
  deferSaveToAddressBookSwitch: false,
  saveToAddressBookSwitchChecked: undefined,
  onSaveToAddressBookSwitchIntentChange: undefined,
};

export default inject('checkoutStore')(
  injectIntl(observer(CheckoutDeliveryForm))
);
